export namespace FormatUtils {
	export interface TimeNames {
		seconds: [string, string];
		minutes: [string, string];
		hours: [string, string];
		days: [string, string];
		months: [string, string];
		years: [string, string];
	}
}
export class FormatUtils {
	public static fileSizesShort = [
		" B", " KB", " MB", " GB", " TB", " PB", " EB", " ZB", " YB"
	];
	public static fileSizesShortNoSpace = [
		"B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"
	];
	public static fileSizesLong = [
		" bytes", " kilobytes", " megabytes", " gigabytes", " terabytes", " petabytes", " exabytes", " zettabytes", " yottabytes"
	];

	public static fileSize(size: number, names: string[]) {
		let negative = size < 0;
		size = Math.abs(size);
		let i = Math.floor(Math.log(size) / Math.log(1024) + 0.336);
		let value = +((size / Math.pow(1024, i)).toFixed(2));
		return `${negative ? "-" : ""}${(value).toString().substring(0, 4)}${names[i] ?? " ??"}`;
	}

	public static fileSizeShort(size: number) {
		return FormatUtils.fileSize(size, FormatUtils.fileSizesShort);
	}

	public static fileSizeShortNoSpace(size: number) {
		return FormatUtils.fileSize(size, FormatUtils.fileSizesShortNoSpace);
	}

	public static fileSizeLong(size: number) {
		return FormatUtils.fileSize(size, FormatUtils.fileSizesLong);
	}

	public static timeShort: FormatUtils.TimeNames = {
		"seconds": ["sec", "secs"],
		"minutes": ["min", "mins"],
		"hours": ["hour", "hours"],
		"days": ["day", "days"],
		"months": ["month", "months"],
		"years": ["year", "years"]
	};

	public static timeLong: FormatUtils.TimeNames = {
		"seconds": ["second", "seconds"],
		"minutes": ["minute", "minutes"],
		"hours": ["hour", "hours"],
		"days": ["day", "days"],
		"months": ["month", "months"],
		"years": ["year", "years"]
	};

	public static timeAgo(diffOrDate: Date | number, names: FormatUtils.TimeNames = FormatUtils.timeLong) {
		let diff;
		if (diffOrDate instanceof Date) {
			diff = Date.now() - diffOrDate.getTime();
		} else {
			diff = diffOrDate;
		}
		let seconds = Math.floor(diff / 1000);
		let minutes = Math.floor(seconds / 60);
		let hours = Math.floor(minutes / 60);
		let days = Math.floor(hours / 24);
		let months = Math.floor(days / 30);
		let years = Math.floor(months / 12);

		if (years > 0) {
			return `${years} ${years === 1 ? names.years[0] : names.years[1]}`;
		} else if (months > 0) {
			return `${months} ${months === 1 ? names.months[0] : names.months[1]}`;
		} else if (days > 0) {
			return `${days} ${days === 1 ? names.days[0] : names.days[1]}`;
		} else if (hours > 0) {
			return `${hours} ${hours === 1 ? names.hours[0] : names.hours[1]}`;
		} else if (minutes > 0) {
			return `${minutes} ${minutes === 1 ? names.minutes[0] : names.minutes[1]}`;
		} else if (seconds >= 0) {
			return `${seconds} ${seconds === 1 ? names.seconds[0] : names.seconds[1]}`;
		} else {
			return false;
		}
	}

	public static timeFromNow(diffOrDate: Date | number, names: FormatUtils.TimeNames = FormatUtils.timeLong) {
		let diff;
		if (diffOrDate instanceof Date) {
			diff = diffOrDate.getTime() - Date.now();
		} else {
			diff = diffOrDate;
		}

		let seconds = Math.floor(diff / 1000);
		let minutes = Math.floor(seconds / 60);
		let hours = Math.floor(minutes / 60);
		let days = Math.floor(hours / 24);
		let months = Math.floor(days / 30);
		let years = Math.floor(months / 12);

		if (years > 0) {
			return `${years} ${years === 1 ? names.years[0] : names.years[1]}`;
		} else if (months > 0) {
			return `${months} ${months === 1 ? names.months[0] : names.months[1]}`;
		} else if (days > 0) {
			return `${days} ${days === 1 ? names.days[0] : names.days[1]}`;
		} else if (hours > 0) {
			return `${hours} ${hours === 1 ? names.hours[0] : names.hours[1]}`;
		} else if (minutes > 0) {
			return `${minutes} ${minutes === 1 ? names.minutes[0] : names.minutes[1]}`;
		} else if (seconds >= 0) {
			return `${seconds} ${seconds === 1 ? names.seconds[0] : names.seconds[1]}`;
		} else {
			return false;
		}
	}

	public static time(diffOrDate: Date | number, names: FormatUtils.TimeNames = FormatUtils.timeLong) {
		let diff;
		if (diffOrDate instanceof Date) {
			diff = diffOrDate.getTime() - Date.now();
		} else {
			diff = diffOrDate;
		}

		let seconds = Math.floor(diff / 1000);

		if (seconds === 0) {
			return "now";
		} else if (diff > 0) {
			return FormatUtils.timeFromNow(diff, names) + " from now";
		} else {
			return FormatUtils.timeAgo(-diff, names) + " ago";
		}
	}

	public static numberShortNames = [
		"", "K", "M", "B", "T", "Q"
	];

	public static numberLongNames = [
		"", " thousand", " million", " billion", " trillion", " quadrillion"
	];

	public static number(value: number, names: string[]) {
		let negative = value < 0;
		value = Math.abs(value);
		let i = value > 0 ? Math.floor(Math.log(value) / Math.log(1000) + 0.334) : 0;
		let value2 = +(value / Math.pow(1000, i)).toFixed(2);
		return `${negative ? "-" : ""}${(value2).toString().substring(0, 4)}${names[i]}`;
	}

	public static numberShort(value: number) {
		return FormatUtils.number(value, FormatUtils.numberShortNames);
	}

	public static numberLong(value: number) {
		return FormatUtils.number(value, FormatUtils.numberLongNames);
	}
}